<script>
	import { format, max, scaleBand, scaleLinear } from '@vpap_svelte/charts/d3';
	import { AppContext } from './Context.svelte';
	import {
		AxisX,
		AxisY,
		ChartContext,
		Columns,
		DataTooltip,
		Html,
		Svg,
		TooltipState,
	} from '@vpap_svelte/charts';

	let dims = ChartContext.get();
	let ctx = AppContext.get();

	let yScale = $derived.by(() => {
		return scaleLinear()
			.domain([0, max(ctx.byGovernor.values())])
			.range([dims.chart.height, 0]);
	});
	let xScale = $derived.by(() => {
		return scaleBand().domain(ctx.governorOrder).range([0, dims.chart.width]).padding(0.1);
	});

	let tooltip = new TooltipState();

	$inspect(tooltip.data);
</script>

<Html class="pointer-none">
	<AxisY scale={yScale} ticks={yScale.ticks(4)} position="left" units="px">
		{#snippet label(tick)}
			<div class="axis-y-label">{format('$.1s')(tick)}</div>
		{/snippet}
		{#snippet line(tick)}
			<div class={['axis-y-line', tick === 0 && 'baseline']}></div>
		{/snippet}
	</AxisY>
	<AxisX scale={xScale} ticks={ctx.governorOrder} position="bottom" units="px">
		{#snippet label(tick, idx)}
			{@const govYr = ctx.data.find((e) => e.candidate_name === tick).election_year}
			<div
				class="cand-label"
				style:transform="translate({xScale.bandwidth() / 2}px, calc(100% + .25rem))"
			>
				{#if !ctx.isMobile}
					<div class="cand-name">
						{#if ctx.isMobile}
							{@const sp = tick.split(' ')}
							{sp[sp.length - 1]}
						{:else}
							{tick}
						{/if}
					</div>
				{/if}
				<div class="cand-yr">{govYr}</div>
			</div>
		{/snippet}
	</AxisX>
</Html>
<Svg class="pointer-none">
	<Columns data={[...ctx.byGovernor.entries()]} xScale={{ scale: xScale, accessor: (d) => d[0] }}>
		{#snippet children(datum)}
			{@const d = datum.datum}
			{@const party = ctx.data.find((e) => e.candidate_name === d[0]).party}
			{@const govYr = ctx.data.find((e) => e.candidate_name === d[0]).election_year}
			{@const tipData = { governor: d[0], amount: d[1], year: govYr }}
			{#if ctx.isMobile}
				<rect
					width={xScale.bandwidth()}
					stroke="none"
					class="pointer-all"
					fill={party === 'Republican' ? '#D65454' : '#3989CB'}
					height={yScale(0) - yScale(d[1])}
					y={yScale(d[1])}
					tabindex="0"
					onmouseover={() => {
						console.log('hi');
						tooltip.mouseover(tipData);
						ctx.tipChart = 'column';
					}}
					onmousemove={(e) => {
						tooltip.mousemove(e);
					}}
					onmouseleave={() => {
						tooltip.hide();
						ctx.tipChart = undefined;
					}}
					onfocus={(e) => {
						tooltip.focus(e, tipData);
						ctx.tipChart = 'column';
					}}
					onblur={() => {
						tooltip.hide();
						ctx.tipChart = undefined;
					}}
					onkeydown={(e) => {
						if (e.code === 'Space') {
							tooltip.show();
						} else if (e.code === 'Escape') {
							tooltip.hide();
						}
					}}
				></rect>
			{:else}
				<rect
					width={xScale.bandwidth()}
					stroke="none"
					class="pointer-all"
					fill={party === 'Republican' ? '#D65454' : '#3989CB'}
					height={yScale(0) - yScale(d[1])}
					y={yScale(d[1])}
				></rect>
			{/if}
			<text
				class="donor-label pointer-none"
				fill="#fff"
				text-anchor="middle"
				x={xScale.bandwidth() / 2}
				y={yScale(d[1]) + 16}
			>
				{format('$.2~s')(d[1])}
			</text>
		{/snippet}
	</Columns>
</Svg>
{#if ctx.isMobile}
	<Html class="pointer-none">
		{#if tooltip.data && ctx.tipChart === 'column'}
			<DataTooltip
				id="g-tooltip"
				x={0}
				y={0}
				position="fixed-bottom"
				bind:visible={tooltip.visible}
				style={{ width: '100%', 'z-index': 2, 'pointer-events': 'all' }}
			>
				<div class="tip-head">
					<div class="tip-heading">
						{tooltip.data.governor}
					</div>
					<div class="tip-yr">{tooltip.data.year}</div>
				</div>
				<div class="tip-contents">
					{format('$,.0f')(tooltip.data.amount)}
				</div>
			</DataTooltip>
		{/if}
	</Html>
{/if}

<style>
	.cand-label {
		display: flex;
		flex-direction: column;
		align-items: center;
		font-size: 0.85rem;
	}

	.axis-y-label {
		font-size: 0.85rem;
		color: #42585c;
		padding-right: 2px;
	}

	.axis-y-line {
		width: 100%;
		height: 0;
		border-top: 1px solid #ccc;
	}

	.baseline {
		border-bottom: 1px solid #42585c;
		border-color: #42585c;
	}

	.donor-label {
		font-size: 1.125rem;
		font-weight: bold;
	}

	@media only screen and (max-width: 35em) {
		.donor-label {
			font-size: 0.85rem;
		}
	}

	.tip-head {
		text-align: center;
		margin: 0.25rem;
		border-bottom: 1px solid #42585c;
	}

	.tip-heading {
		font-size: 1rem;
		color: #42585c;
	}

	.tip-yr {
		font-size: 0.85rem;
		color: #555;
	}

	.tip-contents {
		font-size: 0.85rem;
		margin: 0.25rem 1rem;
		text-align: center;
	}
</style>
